import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "../components/ui/dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../components/ui/table";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { Wallet, Receipt, CurrencyCircleDollar, Plus, Spinner, Check, Clock, Lightning, Trash } from "@phosphor-icons/react";
import { toast } from "sonner";
import { getErrorMessage } from "../lib/utils";
import { useTranslation } from "react-i18next";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../components/ui/alert-dialog";
import Pagination from "../components/Pagination";

const API_URL = process.env.REACT_APP_BACKEND_URL;
const lira = "\u20ba";

const AdminFinancePage = () => {
  const { t } = useTranslation();
  const [summary, setSummary] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [subSummary, setSubSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [invoiceDialog, setInvoiceDialog] = useState(false);
  const [formLoading, setFormLoading] = useState(false);
  const [invoiceForm, setInvoiceForm] = useState({
    customer_id: "",
    period: new Date().toISOString().slice(0, 7),
    pos_commission_rate: 2.5,
    operating_cost_rate: 5.0,
  });
  const [deleteDialog, setDeleteDialog] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [invPage, setInvPage] = useState(1);
  const [invPageSize, setInvPageSize] = useState(20);

  const fetchData = useCallback(async () => {
    try {
      const [summaryRes, invoicesRes, subSumRes, billingRes] = await Promise.all([
        axios.get(`${API_URL}/api/admin/financial-summary`),
        axios.get(`${API_URL}/api/admin/invoices`),
        axios.get(`${API_URL}/api/admin/subscription-summary`).catch(() => ({ data: null })),
        axios.get(`${API_URL}/api/admin/billing-settings`).catch(() => ({ data: null })),
      ]);
      setSummary(summaryRes.data);
      setInvoices(invoicesRes.data);
      setSubSummary(subSumRes.data);
      if (billingRes.data) {
        setInvoiceForm(prev => ({ ...prev, pos_commission_rate: billingRes.data.pos_commission_rate }));
      }
    } catch {
      toast.error("Veri yuklenirken hata");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleGenerateInvoice = async (e) => {
    e.preventDefault();
    setFormLoading(true);
    try {
      await axios.post(`${API_URL}/api/admin/invoices/generate`, invoiceForm);
      toast.success("Fatura olusturuldu");
      setInvoiceDialog(false);
      fetchData();
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setFormLoading(false);
    }
  };

  const handlePayInvoice = async (invoiceId) => {
    try {
      await axios.put(`${API_URL}/api/admin/invoices/${invoiceId}/pay`);
      toast.success("Fatura odendi olarak isaretlendi");
      fetchData();
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  const handleDeleteInvoice = async () => {
    if (!deleteTarget) return;
    try {
      await axios.delete(`${API_URL}/api/admin/invoices/${deleteTarget.id}`);
      toast.success(t("invoices.deleted_success"));
      setInvoices(prev => prev.filter(inv => inv.id !== deleteTarget.id));
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setDeleteDialog(false);
      setDeleteTarget(null);
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64"><Spinner size={32} className="animate-spin text-indigo-600" /></div>;
  }

  const totalRevenue = (subSummary?.total_revenue || 0) + (summary?.monthly_topup || 0);

  return (
    <div className="space-y-6" data-testid="admin-finance-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-heading font-bold text-slate-900">{t("finance.title")}</h1>
          <p className="text-slate-500 mt-1">{t("finance.subtitle")}</p>
        </div>
        <Button className="bg-indigo-600 hover:bg-indigo-700" onClick={() => setInvoiceDialog(true)} data-testid="generate-invoice-btn">
          <Plus size={16} className="mr-2" /> {t("finance.generate")}
        </Button>
      </div>

      {/* Stats - Combined Revenue */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        {[
          { label: t("finance.total_revenue"), value: `${lira}${totalRevenue.toFixed(2)}`, icon: Wallet, bg: "bg-emerald-50", text: "text-emerald-600" },
          { label: t("finance.sub_revenue"), value: `${lira}${(subSummary?.total_revenue || 0).toFixed(2)}`, icon: Lightning, bg: "bg-purple-50", text: "text-purple-600" },
          { label: t("finance.monthly_load"), value: `${lira}${summary?.monthly_topup?.toFixed(2) || "0"}`, icon: CurrencyCircleDollar, bg: "bg-indigo-50", text: "text-indigo-600" },
          { label: t("finance.total_invoice"), value: summary?.invoices_count || 0, icon: Receipt, bg: "bg-amber-50", text: "text-amber-600" },
          { label: t("finance.pending"), value: (summary?.pending_invoices || 0) + (subSummary?.pending_payments || 0), icon: Clock, bg: "bg-red-50", text: "text-red-600" },
        ].map(({ label, value, icon: Icon, bg, text }) => (
          <Card key={label} className="border-slate-200 shadow-sm">
            <CardContent className="p-4 flex items-center justify-between">
              <div>
                <p className="text-xs font-medium text-slate-500">{label}</p>
                <p className="text-xl font-bold text-slate-900 mt-1">{value}</p>
              </div>
              <div className={`w-10 h-10 ${bg} rounded-xl flex items-center justify-center`}>
                <Icon size={20} weight="duotone" className={text} />
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Subscription Revenue Summary */}
      {subSummary && (subSummary.total_revenue > 0 || subSummary.pending_amount > 0) && (
        <Card className="border-slate-200 shadow-sm border-l-4 border-l-purple-400">
          <CardContent className="p-4">
            <div className="flex items-center gap-3 mb-3">
              <Lightning size={18} weight="fill" className="text-purple-600" />
              <h3 className="text-sm font-heading font-bold text-slate-700">{t("finance.revenue_summary")}</h3>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
              <div>
                <p className="text-slate-400 text-xs">{t("finance.activation_revenue")}</p>
                <p className="font-bold text-slate-800">{lira}{subSummary.total_activation.toFixed(2)}</p>
              </div>
              <div>
                <p className="text-slate-400 text-xs">{t("finance.upgrade_revenue")}</p>
                <p className="font-bold text-slate-800">{lira}{subSummary.total_upgrade.toFixed(2)}</p>
              </div>
              <div>
                <p className="text-slate-400 text-xs">{t("finance.pending_payment")}</p>
                <p className="font-bold text-amber-600">{lira}{subSummary.pending_amount.toFixed(2)}</p>
              </div>
              <div>
                <p className="text-slate-400 text-xs">{t("finance.active_sub")}</p>
                <p className="font-bold text-emerald-600">{subSummary.active_subscriptions}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Customer Revenue */}
      <Card className="border-slate-200 shadow-sm">
        <CardHeader>
          <CardTitle className="text-lg font-heading">{t("finance.customer_revenue")}</CardTitle>
        </CardHeader>
        <CardContent>
          {summary?.customer_revenue?.length > 0 ? (
            <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead>{t("common.customer")}</TableHead>
                  <TableHead>{t("common.status")}</TableHead>
                  <TableHead>{t("admin.tenant_header")}</TableHead>
                  <TableHead>{t("admin.balance_header")}</TableHead>
                  <TableHead>{t("admin.monthly_load")}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {summary.customer_revenue.map((c) => (
                  <TableRow key={c.id}>
                    <TableCell className="font-medium">{c.name} {c.company_name && <span className="text-slate-400 text-xs ml-1">({c.company_name})</span>}</TableCell>
                    <TableCell>
                      <Badge className={c.is_active ? "bg-emerald-100 text-emerald-700" : "bg-red-100 text-red-700"}>
                        {c.is_active ? t("common.active") : t("common.inactive")}
                      </Badge>
                    </TableCell>
                    <TableCell>{c.tenant_count}</TableCell>
                    <TableCell>{lira}{c.total_balance.toFixed(2)}</TableCell>
                    <TableCell>{lira}{c.monthly_topup.toFixed(2)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            </div>
          ) : (
            <p className="text-sm text-slate-400 text-center py-6">{t("finance.no_data")}</p>
          )}
        </CardContent>
      </Card>

      {/* Invoices */}
      <Card className="border-slate-200 shadow-sm">
        <CardHeader>
          <CardTitle className="text-lg font-heading">{t("fixed_expenses.invoices_tab")}</CardTitle>
        </CardHeader>
        <CardContent>
          {invoices.length === 0 ? (
            <div className="text-center py-8">
              <Receipt size={40} className="mx-auto text-slate-300 mb-3" />
              <p className="text-sm text-slate-500">{t("finance.no_invoices")}</p>
            </div>
          ) : (
            <>
            <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead>{t("common.customer")}</TableHead>
                  <TableHead>{t("common.period")}</TableHead>
                  <TableHead>{t("common.gross_income")}</TableHead>
                  <TableHead className="hidden md:table-cell">{t("finance.pos_commission")}</TableHead>
                  <TableHead className="hidden md:table-cell">{t("common.operating_cost")}</TableHead>
                  <TableHead>{t("common.net_amount")}</TableHead>
                  <TableHead>{t("common.status")}</TableHead>
                  <TableHead>{t("common.actions")}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {invoices.slice((invPage - 1) * invPageSize, invPage * invPageSize).map((inv) => (
                  <TableRow key={inv.id} data-testid={`invoice-${inv.id}`}>
                    <TableCell className="font-medium">{inv.customer_name}</TableCell>
                    <TableCell>{inv.period}</TableCell>
                    <TableCell>{lira}{inv.gross_revenue.toFixed(2)}</TableCell>
                    <TableCell className="text-red-600 hidden md:table-cell">-{lira}{inv.pos_commission.toFixed(2)}</TableCell>
                    <TableCell className="text-red-600 hidden md:table-cell">-{lira}{inv.operating_cost.toFixed(2)}</TableCell>
                    <TableCell className="font-bold">{lira}{inv.net_amount.toFixed(2)}</TableCell>
                    <TableCell>
                      <Badge className={inv.status === "paid" ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}>
                        {inv.status === "paid" ? t("fixed_expenses.paid") : t("fixed_expenses.pending")}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        {inv.status === "pending" && (
                          <Button size="sm" variant="ghost" className="text-emerald-600" onClick={() => handlePayInvoice(inv.id)} data-testid={`pay-invoice-${inv.id}`}>
                            <Check size={14} className="mr-1" /> {t("finance.pay")}
                          </Button>
                        )}
                        <Button size="sm" variant="ghost" className="text-red-500 hover:text-red-700 h-7 w-7 p-0"
                          onClick={() => { setDeleteTarget(inv); setDeleteDialog(true); }}
                          data-testid={`delete-invoice-${inv.id}`}
                          title={t("invoices.delete")}>
                          <Trash size={14} />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            </div>
            {invoices.length > invPageSize && (
              <div className="pt-4">
                <Pagination total={invoices.length} page={invPage} pageSize={invPageSize} onPageChange={setInvPage} onPageSizeChange={setInvPageSize} />
              </div>
            )}
            </>
          )}
        </CardContent>
      </Card>

      {/* Generate Invoice Dialog */}
      <Dialog open={invoiceDialog} onOpenChange={setInvoiceDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{t("fixed_expenses.generate_invoices")}</DialogTitle>
            <DialogDescription>{t("admin.invoice_desc")}</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleGenerateInvoice}>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>{t("common.customer")}</Label>
                <Select value={invoiceForm.customer_id} onValueChange={(v) => setInvoiceForm({ ...invoiceForm, customer_id: v })}>
                  <SelectTrigger data-testid="invoice-customer-select"><SelectValue placeholder={t("common.select_customer")} /></SelectTrigger>
                  <SelectContent>
                    {summary?.customer_revenue?.map((c) => (
                      <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>{t("admin.period_label")}</Label>
                <Input value={invoiceForm.period} onChange={(e) => setInvoiceForm({ ...invoiceForm, period: e.target.value })} placeholder="2026-03" data-testid="invoice-period-input" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label>POS {t("billing.commission_rate")}</Label>
                  <Input type="number" step="0.1" value={invoiceForm.pos_commission_rate} onChange={(e) => setInvoiceForm({ ...invoiceForm, pos_commission_rate: parseFloat(e.target.value) })} data-testid="commission-input" />
                </div>
                <div className="space-y-2">
                  <Label>{t("admin.operating_cost_label")}</Label>
                  <Input type="number" step="0.1" value={invoiceForm.operating_cost_rate} onChange={(e) => setInvoiceForm({ ...invoiceForm, operating_cost_rate: parseFloat(e.target.value) })} data-testid="operating-cost-input" />
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setInvoiceDialog(false)}>{t("common.cancel")}</Button>
              <Button type="submit" className="bg-indigo-600 hover:bg-indigo-700" disabled={formLoading || !invoiceForm.customer_id} data-testid="invoice-submit-btn">
                {formLoading ? t("common.creating") : t("fixed_expenses.generate_invoices")}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Invoice Confirmation */}
      <AlertDialog open={deleteDialog} onOpenChange={setDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("invoices.delete_confirm_title")}</AlertDialogTitle>
            <AlertDialogDescription>
              {deleteTarget && (
                <span>{deleteTarget.customer_name} - {deleteTarget.period} ({lira}{deleteTarget.net_amount?.toFixed(2)})<br/></span>
              )}
              {t("invoices.delete_confirm_desc")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("common.cancel")}</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteInvoice} className="bg-red-600 hover:bg-red-700" data-testid="confirm-delete-invoice">
              {t("common.delete")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default AdminFinancePage;
